/* eslint @typescript-eslint/no-explicit-any: 0 */
/** NOTE: while we don't have the Angelou Table component typed, we need to use a few any definitions here */

import { ReactElement, useEffect, useState } from "react";

import { LoadingSpinner, Table, Toast } from "@newsela/angelou";
import { isEmpty } from "@s-libs/micro-dash";

import { useMutation } from "@tanstack/react-query";

import api, { ApiCatalog } from "@/api";
import { School } from "@/api/catalogs/mockData/schoolThroughUserMock";
import globalToasterRef from "@/base/globalToasterRef";
import BannerToDisplayStatusOfDownloadRequest from "@/directory/AdminConsoleDirectoryTable/BannerToDisplayStatusOfDownloadRequest";
import ButtonToRequestDirectoryCsv from "@/directory/AdminConsoleDirectoryTable/ButtonToRequestDirectoryCsv";
import SchoolSelector from "@/directory/DirectoryTable/SchoolSelector/SchoolSelector";
import {
  DIRECTORY_CSV_GENERATION_STATUS,
  ApiResponseToCsvGenerationRequest,
} from "@/directory/types";

import { User } from "@/types/models/User/v3/User.interface";
import {
  directoryTableCss,
  filterAndDownloadCss,
  totalResultsStringCss,
  totalResultsWrapperCss,
} from "../DirectoryTable/DirectoryTable.style";
import Filter from "../DirectoryTable/Filter";
import NotFound from "../DirectoryTable/NotFound";
import DownloadSearchResultsButton from "./DownloadSearchResultsButton";

/* TODO - After updating Angelou Table to @tanstack/react-table v8, revisit this type and
 get TData from the generic type set while using the Table component. e.g Table<MyDataType> */
type TData = unknown;

export interface TanStackColumnDef {
  id?: string;
  accessor?: string;
  accessorFn?: (originalRow: TData, index: number) => any;

  columns?: TanStackColumnDef[];

  Header?:
    | string
    | ((props: {
        table: ReactElement;
        header: ReactElement;
        column: ReactElement;
      }) => unknown);

  Footer?:
    | string
    | ((props: {
        table: ReactElement;
        header: ReactElement;
        column: ReactElement;
      }) => unknown);

  Cell?: (cell: any) => unknown;
  disableSortBy?: boolean;
}

export interface DirectoryTableProps {
  data: any[];
  columns: TanStackColumnDef[];
  title: string;
  pageSize: number;
  loading?: boolean;
  filterUIProps: {
    searchFieldLabel: string;
    searchFieldPlaceholder: string;
  };
  filterDefinitions?: Record<string, any>;
  readQuerySearch?: boolean;
  onControlsUpdateCb?: (...args: any[]) => void;
  notFoundMessage?: ReactElement;
  loadingMessage?: ReactElement;
  totalCount?: number;
  useManualUpdate?: boolean;
  initialState?: Record<string, any>;
  shouldDisplayTotalResultsString?: boolean;
  onChangeSchool?: (school: School | undefined | null) => void;
  displayErrorMessage: boolean;
  userData: User | undefined | null;
  responseFromStatusApi: ApiResponseToCsvGenerationRequest | undefined;
  refetchStatusToGenerateCsv: () => void;
  refetchingStatusApiResponse: boolean;
}

enum DirectoryTypeEnum {
  "School" = "schools",
  "Class" = "classrooms",
  "Faculty" = "faculty",
  "Student" = "students",
}

const AdminConsoleDirectoryTable = ({
  title,
  columns,
  pageSize,
  filterUIProps,
  filterDefinitions,
  readQuerySearch,
  data,
  loading,
  loadingMessage,
  notFoundMessage,
  onControlsUpdateCb,
  totalCount,
  useManualUpdate = true,
  initialState = {},
  shouldDisplayTotalResultsString = false,
  onChangeSchool,
  displayErrorMessage,
  userData,
  responseFromStatusApi,
  refetchingStatusApiResponse,
  refetchStatusToGenerateCsv,
}: DirectoryTableProps): ReactElement => {
  const userUniqueId = userData?.unique_id || "";

  const count = totalCount || 0;
  const hasFilterButton =
    !isEmpty(filterUIProps) && !isEmpty(filterDefinitions);

  const isNotFound = !loading && isEmpty(data) && notFoundMessage;
  const isDefaultLoading = loading && !loadingMessage;

  const [hasClickedSearchButton, setHasClickedSearchButton] = useState(false);

  const [
    isBannerForCSVGenerationStatusVisible,
    setIsBannerForCSVGenerationStatusVisible,
  ] = useState(false);

  const directoryType =
    DirectoryTypeEnum[title?.split(" ")[0] as keyof typeof DirectoryTypeEnum];

  // Make a POST request to the Generation Api to initiate the process of generating a csv of the directory.
  const {
    data: responseFromGenerationApi,
    error: generationApiReturnedAnError,
    mutate,
  } = useMutation({
    mutationFn: async () =>
      await api().post<ApiResponseToCsvGenerationRequest>(
        ApiCatalog.adminConsoleDirectoryCsvGeneration.endpoint
          .replace("[userUniqueId]", userUniqueId)
          .replace("[directoryType]", directoryType),
        {},
      ),
  });

  const { csv_status: csvStatusFromStatusApi } = responseFromStatusApi || {};
  const { csv_status: csvStatusFromGenerationApi } =
    responseFromGenerationApi || {};

  const [directoryCsvGenerationStatus, setDirectoryCsvGenerationStatus] =
    useState<undefined | DIRECTORY_CSV_GENERATION_STATUS>();

  useEffect(() => {
    if (csvStatusFromGenerationApi) {
      setDirectoryCsvGenerationStatus(csvStatusFromGenerationApi);
    }
  }, [responseFromGenerationApi]);

  useEffect(() => {
    if (csvStatusFromStatusApi) {
      setDirectoryCsvGenerationStatus(csvStatusFromStatusApi);
    }
  }, [responseFromStatusApi]);

  const csvGenerationError = Boolean(generationApiReturnedAnError);

  useEffect(() => {
    if (
      directoryCsvGenerationStatus &&
      directoryCsvGenerationStatus !== DIRECTORY_CSV_GENERATION_STATUS.NONE
    ) {
      setIsBannerForCSVGenerationStatusVisible(true);
    }
  }, [directoryCsvGenerationStatus]);

  return (
    <>
      {onChangeSchool && <SchoolSelector onChangeSchool={onChangeSchool} />}
      <Table
        title={title}
        columns={columns}
        data={loading ? [] : data || []}
        initialState={{ pageSize, ...initialState }}
        useManualControls={useManualUpdate}
        onControlsUpdate={(
          controls: Record<string, any>,
          rawProps: Record<string, any>,
        ): void | boolean => {
          const searchBoxFilterValue =
            controls["globalFilter"]?.searchBoxFilterValue || {};

          if (useManualUpdate && onControlsUpdateCb) {
            onControlsUpdateCb(controls, rawProps);

            return searchBoxFilterValue !== ""
              ? setHasClickedSearchButton(true)
              : setHasClickedSearchButton(false);
          } else {
            return false;
          }
        }}
        manualPagination={useManualUpdate}
        paginationOptions={[]}
        pageCount={Math.ceil(count / pageSize)}
        ariaLivePagination={(
          state: { pageIndex: number },
          tableProps: { pageOptions: string | any[] },
        ) => `page ${state.pageIndex + 1} of ${tableProps.pageOptions.length}`}
        generateTotalResultsString={(totalResultsCount: string) => {
          return shouldDisplayTotalResultsString && !loading ? (
            <div css={totalResultsWrapperCss}>
              <div css={totalResultsStringCss}>{totalResultsCount} results</div>
              {hasClickedSearchButton && count > 0 ? (
                <DownloadSearchResultsButton />
              ) : null}
            </div>
          ) : null;
        }}
        __renderAboveTable={() => {
          if (loading && loadingMessage) return loadingMessage;
          return null;
        }}
        __renderBelowTable={() => {
          if (displayErrorMessage) {
            return (
              <div>
                There was an error retrieving data. Refresh the page to try
                again.
              </div>
            );
          }
          if (isDefaultLoading) return <LoadingSpinner />;
          if (isNotFound) return notFoundMessage;
          return null;
        }}
        totalItems={count}
        __renderGlobalFilters={(filterProps: {
          setGlobalFilter: (filters: Record<string, any>) => void;
          globalFilter: Record<string, any>;
        }) => {
          // TODO: Remove the comment of the filter definitions validation until the backend implements filtering for the endpoints
          const renderComponent = !isEmpty(filterUIProps); // && !isEmpty(filterDefinitions);

          if (!filterDefinitions) {
            filterDefinitions = {};
          }

          return renderComponent ? (
            <div css={filterAndDownloadCss}>
              <Filter
                {...{
                  filterProps,
                  filterUIProps,
                  filterDefinitions,
                  readQuerySearch,
                }}
              />
              <>
                <ButtonToRequestDirectoryCsv
                  isCsvGenerationInProgress={
                    directoryCsvGenerationStatus ===
                    DIRECTORY_CSV_GENERATION_STATUS.IN_PROGRESS
                  }
                  onClick={() => {
                    if (
                      directoryCsvGenerationStatus ===
                      DIRECTORY_CSV_GENERATION_STATUS.IN_PROGRESS
                    ) {
                      globalToasterRef.current.displayToast({
                        showIcon: true,
                        statusColor: Toast.statusColor.danger,
                        children:
                          "A csv file is already being generated for this directory",
                      });
                    } else {
                      mutate();
                    }
                  }}
                />
                <BannerToDisplayStatusOfDownloadRequest
                  refetchingStatusApiResponse={refetchingStatusApiResponse}
                  directoryType={directoryType}
                  directoryCsvGenerationStatus={directoryCsvGenerationStatus}
                  downloadUrl={responseFromStatusApi?.presigned_url || ""}
                  error={csvGenerationError}
                  isVisible={isBannerForCSVGenerationStatusVisible}
                  setIsVisible={() =>
                    setIsBannerForCSVGenerationStatusVisible(false)
                  }
                  onButtonClick={refetchStatusToGenerateCsv}
                />
              </>
            </div>
          ) : null;
        }}
        __cssFor={directoryTableCss({
          hasFilterButton,
          hideTable: Boolean(isNotFound || isDefaultLoading),
        })}
      />
    </>
  );
};

AdminConsoleDirectoryTable.NotFound = NotFound;

export default AdminConsoleDirectoryTable;
